import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StarRating from "@/components/StarRating";
import Seo from "@/components/Seo";
import { ArrowLeft, ArrowSquareOut, Sparkle, ChatCircle, CursorClick, PaperPlaneRight, XLogo, FacebookLogo, WhatsappLogo, LinkSimple, Fire } from "@phosphor-icons/react";

const FALLBACK = "https://images.unsplash.com/photo-1774271074342-93257fc7486f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [link, setLink] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [comments, setComments] = useState([]);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const [l, r, c] = await Promise.all([
      api.get(`/links/${id}`),
      api.get(`/links/${id}/reviews`),
      api.get(`/links/${id}/comments`),
    ]);
    setLink(l.data); setReviews(r.data); setComments(c.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const visit = async () => {
    const { data } = await api.post(`/links/${id}/click`);
    window.open(data.url, "_blank", "noopener");
  };

  const submitReview = async () => {
    setErr("");
    if (!rating) { setErr("Please pick a star rating."); return; }
    try {
      await api.post(`/links/${id}/reviews`, { rating, comment: reviewText });
      setRating(0); setReviewText("");
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not post review"); }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/links/${id}/comments`, { text: commentText });
      setCommentText(""); load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not post comment"); }
  };

  if (!link) return <div className="min-h-[60vh] flex items-center justify-center text-[#FF007F] font-display uppercase animate-pulse">Loading…</div>;

  const pageUrl = typeof window !== "undefined" ? window.location.href : `/deal/${id}`;
  const shareUrl = encodeURIComponent(pageUrl);
  const shareText = encodeURIComponent(`${link.title}${link.price ? ` — ${link.price}` : ""} 🔥 Deal on Affiliated Connections`);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(pageUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { console.error("Clipboard copy failed:", e); }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const priceAmount = (link.price || "").replace(/[^0-9.]/g, "");
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: link.title,
    description: link.description || link.title,
    image: mediaUrl(link.image_url) || FALLBACK,
    category: link.category_slug?.replace(/-/g, " "),
    ...(priceAmount && {
      offers: {
        "@type": "Offer",
        price: priceAmount,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: pageUrl,
      },
    }),
    ...(link.review_count > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: link.avg_rating,
        reviewCount: link.review_count,
      },
    }),
    review: reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.user_name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating },
      reviewBody: r.comment || "",
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: link.category_slug?.replace(/-/g, " "), item: `${origin}/category/${link.category_slug}` },
      { "@type": "ListItem", position: 3, name: link.title, item: pageUrl },
    ],
  };

  const metaDesc = `${link.title}${link.price ? ` — ${link.price}` : ""}. ${link.description || `A fantastic ${link.category_slug?.replace(/-/g, " ")} affiliate deal`}. Read reviews and grab this deal on Affiliated Connections.`.slice(0, 300);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Seo
        title={link.title}
        description={metaDesc}
        image={mediaUrl(link.image_url) || FALLBACK}
        path={`/deal/${id}`}
        type="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#00FFFF] mb-6">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="relative border border-zinc-800">
          <img src={mediaUrl(link.image_url) || FALLBACK} alt={link.title}
            onError={(e) => { e.currentTarget.src = FALLBACK; }}
            className="w-full h-80 lg:h-full object-cover" />
          <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
            {link.is_sponsored && <span className="flex items-center gap-1 bg-[#FF007F] text-white text-xs font-black uppercase tracking-widest px-2 py-1"><Sparkle size={12} weight="fill" /> Sponsored</span>}
            {link.is_mine && <span className="bg-[#00FFFF] text-black text-xs font-black uppercase tracking-widest px-2 py-1">★ Your Link</span>}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Link to={`/category/${link.category_slug}`} className="text-xs uppercase tracking-widest text-[#00FFFF] font-bold">{link.category_slug?.replace("-", " ")}</Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight">{link.title}</h1>
            {link.click_count >= 15 && (
              <span className="flex items-center gap-1 bg-[#39FF14] text-black text-[11px] font-black uppercase tracking-widest px-2 py-1 animate-pulse" data-testid="detail-trending-badge">
                <Fire size={13} weight="fill" /> Trending
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <StarRating value={link.avg_rating} count={link.review_count} size={20} />
            <span className="flex items-center gap-1 text-sm text-zinc-500 font-mono"><CursorClick size={15} /> {link.click_count} clicks</span>
          </div>
          {link.price && <div className="font-mono font-extrabold text-3xl text-[#39FF14]">{link.price}</div>}
          <p className="text-zinc-300 leading-relaxed">{link.description || "No description provided."}</p>
          {link.owner_name && <p className="text-xs text-zinc-500 font-mono">Posted by {link.owner_name}</p>}
          <button onClick={visit} data-testid="detail-visit-button"
            className="mt-2 flex items-center justify-center gap-2 px-6 py-4 bg-[#FF007F] text-white font-black uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors hard-shadow">
            Visit Deal <ArrowSquareOut size={20} weight="bold" />
          </button>

          <div className="mt-3" data-testid="share-row">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Share this deal</p>
            <div className="flex items-center gap-2">
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-x"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                <XLogo size={16} weight="fill" /> X
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-facebook"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                <FacebookLogo size={16} weight="fill" /> Facebook
              </a>
              <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors">
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
              <button onClick={copyLink} data-testid="share-copy"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#FFD600] hover:text-[#FFD600] transition-colors">
                <LinkSimple size={16} weight="bold" /> {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-14 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-2xl font-black uppercase mb-4">Reviews <span className="text-[#FF007F]">({reviews.length})</span></h2>
          {user ? (
            <div className="border border-zinc-800 p-4 mb-6 bg-[#18181B]">
              <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Rate this deal</p>
              <StarRating value={rating} editable onChange={setRating} size={26} />
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience…" rows={2} data-testid="review-text-input"
                className="w-full mt-3 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none p-2 text-sm text-white" />
              {err && <p className="text-[#FF007F] text-xs mt-2">{err}</p>}
              <button onClick={submitReview} data-testid="submit-review-button"
                className="mt-3 px-4 py-2 bg-[#00FFFF] text-black text-xs font-bold uppercase tracking-widest hover:bg-[#FF007F] hover:text-white transition-colors">
                Post Review
              </button>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mb-6"><Link to="/login" className="text-[#00FFFF] underline">Log in</Link> to leave a review.</p>
          )}
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="border border-zinc-800 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{r.user_name}</span>
                  <StarRating value={r.rating} size={14} />
                </div>
                {r.comment && <p className="text-sm text-zinc-400 mt-2">{r.comment}</p>}
              </div>
            ))}
            {reviews.length === 0 && <p className="text-zinc-500 text-sm">No reviews yet. Be the first!</p>}
          </div>
        </div>

        {/* Comments */}
        <div>
          <h2 className="font-display text-2xl font-black uppercase mb-4 flex items-center gap-2"><ChatCircle size={24} weight="fill" color="#00FFFF" /> Discussion <span className="text-[#00FFFF]">({comments.length})</span></h2>
          {user ? (
            <div className="flex gap-2 mb-6">
              <input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                placeholder="Add a comment…" data-testid="comment-input"
                className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-2 text-sm text-white" />
              <button onClick={submitComment} data-testid="submit-comment-button"
                className="px-3 bg-[#00FFFF] text-black hover:bg-[#FF007F] hover:text-white transition-colors"><PaperPlaneRight size={18} weight="fill" /></button>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mb-6"><Link to="/login" className="text-[#00FFFF] underline">Log in</Link> to join the discussion.</p>
          )}
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="border-l-2 border-[#FF007F] pl-3 py-1">
                <span className="font-bold text-sm">{c.user_name}</span>
                <p className="text-sm text-zinc-400">{c.text}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-zinc-500 text-sm">No comments yet.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
